<template>
  <!-- Seaded -> Kliendid uues kujunduses: the new look. Off, the old table. -->
  <duplicates-plate v-if="plate" />
  <loading-content v-else :loading="loading">
    <heading>
      Kliendid
      <v-btn v-if="selected.length === 2" color="orange-darken-1" class="mr-2" @click="openMerge">Ühenda valitud</v-btn>
      <v-btn @click="openSearch" color="primary">Duplikaatide otsing</v-btn>
    </heading>

    <v-alert v-if="capped" type="warning" class="mb-5" variant="tonal" density="compact">
      Kuvatakse ainult esimesed {{ scanLimit }} klienti. Sellest piirist kaugemal olevaid duplikaate ei tuvastata.
    </v-alert>

    <card class="mb-5" v-if="searchVisible">
      <h3>Duplikaatide otsing</h3>
      <v-row>
        <v-col>
          <v-text-field
            v-model="search.query"
            label="Nimi / telefon / e-post"
            clearable
            hide-details
            variant="outlined"
            density="compact"
          />
        </v-col>
      </v-row>
    </card>

    <v-data-table
      class="table"
      :headers="headers"
      :loading="loading"
      :items="clients"
      v-model:items-per-page="itemsPerPage"
      :items-per-page-options="itemsPerPageOptions"
      v-model="selected"
      show-select
      item-value="id"
      return-object
      density="compact"
    >
      <template v-slot:[`item.fullName`]="{ item }">
        <router-link :to="{ name: 'client/view/info', params: { clientId: item.id } }" :class="{ 'text-red': item.problematic }">
          {{ item.fullName }}
        </router-link>
      </template>
      <template v-slot:[`item.duplicateReasons`]="{ item }">
        <v-chip
          v-for="r in item.duplicateReasons"
          :key="r"
          :color="reasonColor(r)"
          class="mr-1"
          size="x-small"
          variant="flat"
        >{{ reasonLabel(r) }}</v-chip>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn color="primary" @click="$router.push({ name: 'client/edit', params: { clientId: item.id } })" size="x-small">
          Muuda
        </v-btn>
      </template>
    </v-data-table>

    <!-- Merge dialog -->
    <v-dialog v-model="mergeDialog" max-width="560" persistent>
      <v-card v-if="selected.length === 2">
        <v-card-title class="text-h5">Klientide ühendamine</v-card-title>
        <v-card-text>
          <p class="mb-4">Valige, milline klient säilitatakse. Teine klient kustutatakse ja selle objektid ning kalendrikirjed teisaldatakse säilitatavale.</p>
          <v-radio-group v-model="keepId">
            <v-radio v-for="client in selected" :key="client.id" :value="client.id">
              <template v-slot:label>
                <div>
                  <strong>{{ client.fullName }}</strong>
                  <span class="ml-2 text-grey">(id: {{ client.id }})</span>
                  <span v-if="client.phone" class="ml-2 text-grey-darken-1">{{ client.phone }}</span>
                  <span v-if="client.email" class="ml-2 text-grey-darken-1">{{ client.email }}</span>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
          <v-alert type="warning" class="mt-2" variant="tonal" density="compact">
            Kustutatud klient ei ole taastatav. Objektid ja kalendrikirjed teisaldatakse säilitatavale kliendile.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="mergeDialog = false" variant="text">Tühista</v-btn>
          <v-btn color="orange-darken-1" :loading="merging" @click="confirmMerge">Ühenda</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </loading-content>
</template>

<script>
import api from '@/api'
import helpersMixin from '@/plugins/helpersMixin'
import { mapActions, mapState } from 'vuex'
import debounce from 'lodash/debounce'
import DuplicatesPlate from './DuplicatesPlate.vue'

export default {
  components: { DuplicatesPlate },
  mixins: [ helpersMixin ],
  data () {
    return {
      loading: false,
      merging: false,
      clients: [],
      capped: false,
      scanLimit: 0,
      selected: [],
      searchVisible: false,
      mergeDialog: false,
      keepId: null,
      search: { query: '' },
      // 30 to start with: Vuetify 2 opened on the first of the options when its
      // own default (10) was not one of them, and Vuetify 3 keeps the 10.
      itemsPerPage: 30,
      itemsPerPageOptions: [30, 50, 100]
    }
  },
  computed: {
    ...mapState(['features']),
    plate () {
      return !!(this.features && this.features.clientPlate)
    },
    headers () {
      return [{
        title: 'Nimi',
        align: 'start',
        value: 'fullName',
        sortable: false
      }, {
        title: 'Telefon',
        align: 'start',
        value: 'phone',
        sortable: false
      }, {
        title: 'E-post',
        align: 'start',
        value: 'email',
        sortable: false
      }, {
        title: 'Duplikaat',
        align: 'start',
        value: 'duplicateReasons',
        sortable: false
      }, {
        title: '',
        value: 'actions',
        sortable: false,
        align: 'end'
      }]
    }
  },
  watch: {
    'search.query': debounce(function () {
      this.refresh()
    }, 400)
  },
  methods: {
    ...mapActions(['showNotification']),
    reasonLabel (reason) {
      return { phone: 'Telefon', email: 'E-post', name: 'Nimi' }[reason] || reason
    },
    reasonColor (reason) {
      return { phone: 'blue', email: 'green-darken-1', name: 'orange-darken-1' }[reason] || 'grey'
    },
    openSearch () {
      this.searchVisible = true
      this.refresh()
    },
    openMerge () {
      this.keepId = this.selected[0].id
      this.mergeDialog = true
    },
    async confirmMerge () {
      const deleteId = this.selected.find(c => c.id !== this.keepId).id
      this.merging = true
      try {
        const { moved } = await api.mergeClients({ keepId: this.keepId, deleteId })
        this.showNotification({ text: `Ühendamine õnnestus. ${moved} objekti teisaldati.`, color: 'success' })
        this.mergeDialog = false
        this.selected = []
        await this.refresh()
      } catch (err) {
        this.showNotification({ text: err.message || 'Ühendamine ebaõnnestus', color: 'red' })
      } finally {
        this.merging = false
      }
    },
    async refresh () {
      this.loading = true
      try {
        const { clients, capped, scanLimit } = await api.getDuplicateClients({ query: this.search.query || undefined })
        this.clients = clients
        this.capped = capped || false
        this.scanLimit = scanLimit || 0
      } catch (err) {
        this.showNotification({ text: err.message || 'Viga duplikaatide otsimisel', color: 'red' })
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    // The new look scans for itself.
    if (!this.plate) this.refresh()
  }
}
</script>
